/**
 * Hot & Cold Hold — Prep List Fill Screen.
 *
 * Full-screen modal mirroring the Cooking `PrepListFillModal` so an
 * operator has the same muscle memory across kitchen records. Sticky
 * header carries the list title + counts + close; the row below carries
 * the filter pills (All / Awaiting / Verified / Skipped), a live search
 * bar, and a Submit-all button that fires every pending row with a
 * staged temperature in one go.
 *
 * Per-row layout matches cooking: order#, dish + shared start-time,
 * temperature input, camera + upload (OCR), save, and a "more"
 * chevron that reveals Edit-details + Skip-this-dish. Skip requires a
 * reason (product-owner spec, 2026-09-23) — the confirm button stays
 * disabled until the operator types one.
 */
import { useState, useMemo, useRef, useEffect, forwardRef, useImperativeHandle } from "react";
import { createPortal } from "react-dom";
import { X, Camera, Upload, Check, ChevronDown, AlertTriangle, SkipForward, Search, Pencil, Send, Flame, Snowflake } from "lucide-react";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function formatTenantDate(iso, pattern) {
  if (!iso || iso.length < 10) return iso || "";
  const [y, m, d] = iso.slice(0, 10).split("-");
  if (!y || !m || !d) return iso;
  if ((pattern || "").toUpperCase() === "MM/DD/YYYY") return `${m}/${d}/${y}`;
  return `${d}/${m}/${y}`;
}

const MODE_LABEL = { hot: "Hot hold", cold: "Cold hold" };
const HOLD_STATUS_LABEL = {
  pending: "Pending",
  awaiting_verification: "Awaiting verification",
  verified: "Verified",
  skipped: "Skipped",
};
const HOLD_STATUS_CLS = {
  pending: "",
  awaiting_verification: "warn",
  verified: "ok",
  skipped: "",
};

export default function HoldPrepListFillModal({ prepList: initial, config, onClose, onChanged }) {
  const { tenant } = useAuth();
  const tenantDateFormat = (tenant?.operational?.date_format || "DD/MM/YYYY").toUpperCase();
  const [prepList, setPrepList] = useState(initial);
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");

  useEffect(() => { setPrepList(initial); }, [initial]);

  const mode = prepList.mode;
  const holdMin = config?.hot_hold_min_c ?? 63;
  const holdMax = config?.cold_hold_max_c ?? 5;

  const items = prepList.items || [];
  const visible = useMemo(() => {
    let rows = items;
    if (filter === "awaiting_verification") rows = rows.filter((i) => i.batch_status === "awaiting_verification");
    else if (filter === "verified") rows = rows.filter((i) => i.batch_status === "verified");
    else if (filter === "skipped") rows = rows.filter((i) => i.batch_status === "skipped");
    const q = query.trim().toLowerCase();
    if (q) {
      rows = rows.filter((i) =>
        (i.dish || "").toLowerCase().includes(q) ||
        (i.order_no || "").toLowerCase().includes(q),
      );
    }
    return rows;
  }, [items, filter, query]);
  const countBy = (s) => items.filter((i) => i.batch_status === s).length;
  const pendingCount = countBy("pending");
  const awaitingCount = countBy("awaiting_verification");

  // Row refs so Submit-all can drive every pending row imperatively —
  // same trick the cooking flow uses so per-row temp state stays local.
  const rowRefs = useRef({});
  const [bulkBusy, setBulkBusy] = useState(false);
  const stagedCount = items.filter(
    (i) => i.status === "pending" && rowRefs.current[i.id]?.hasStagedTemp?.(),
  ).length;

  const refresh = async () => {
    try {
      const { data } = await api.get(`/hot-cold-hold/hold-prep-lists/${prepList.id}`);
      setPrepList(data.prep_list);
      onChanged?.(data.prep_list);
    } catch { /* silent */ }
  };

  const submitAll = async () => {
    const pending = items.filter((i) => i.status === "pending");
    const eligible = pending.filter((i) => rowRefs.current[i.id]?.hasStagedTemp?.());
    if (!eligible.length) {
      toast.error("No dishes have a temperature entered yet.");
      return;
    }
    setBulkBusy(true);
    let ok = 0, blocked = 0, failed = 0;
    for (const it of eligible) {
      const r = rowRefs.current[it.id];
      if (!r) continue;
      const res = await r.submitSilent();
      if (res.ok) ok++;
      else if (res.blocked) blocked++;
      else failed++;
    }
    setBulkBusy(false);
    const parts = [];
    if (ok) parts.push(`✓ ${ok} logged`);
    if (blocked) parts.push(`${blocked} need corrective action`);
    if (failed) parts.push(`${failed} failed`);
    const msg = parts.join(" · ") || "Nothing to submit.";
    if (failed || blocked) toast.error(msg);
    else toast.success(msg);
    await refresh();
  };

  return createPortal(
    <div className="fixed inset-0" style={{ background: "var(--vb-bg,#f4ede0)", zIndex: 12000, overflowY: "auto" }}
      data-testid="hold-prep-fill-modal">
      <div className="sticky top-0 z-10" style={{ background: "var(--vb-bg-2,#faf6f1)", borderBottom: "1px solid var(--vb-border)" }}>
        <div className="max-w-3xl mx-auto p-3 flex items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="vb-heading text-lg m-0 truncate flex items-center gap-2" style={{ color: "var(--vb-text-2)" }}>
              {mode === "hot"
                ? <Flame size={16} style={{ color: "#dc2626" }} />
                : <Snowflake size={16} style={{ color: "#2563eb" }} />}
              Prep List · {formatTenantDate(prepList.hold_date, tenantDateFormat)}
            </div>
            <div className="text-[11px] opacity-70 truncate">
              {prepList.facility_name} · {MODE_LABEL[mode]}
              {prepList.hold_time ? ` · ${prepList.hold_time}` : ""} · {countBy("verified")}/{items.length} verified
            </div>
          </div>
          <button onClick={onClose} className="vb-btn ghost sm" data-testid="hold-prep-fill-close">
            <X size={16} />
          </button>
        </div>
        <div className="max-w-3xl mx-auto px-3 pb-2 flex flex-col sm:flex-row gap-2 sm:items-center">
          <div className="flex gap-1 overflow-x-auto flex-1" style={{ scrollbarWidth: "thin" }}>
            {[
              ["all", `All · ${items.length}`],
              ["awaiting_verification", `Awaiting · ${awaitingCount}`],
              ["verified", `Verified · ${countBy("verified")}`],
              ["skipped", `Skipped · ${countBy("skipped")}`],
            ].map(([k, label]) => (
              <button key={k}
                className={`vb-btn sm ${filter === k ? "" : "ghost"}`}
                style={{ whiteSpace: "nowrap", flex: "0 0 auto" }}
                onClick={() => setFilter(k)}
                data-testid={`hold-prep-fill-filter-${k}`}>
                {label}
              </button>
            ))}
          </div>
          <div className="relative sm:w-64 shrink-0">
            <Search size={13} className="absolute" style={{ left: 8, top: "50%", transform: "translateY(-50%)", opacity: 0.5 }} />
            <input type="search" placeholder="Search dish or order #"
              value={query} onChange={(e) => setQuery(e.target.value)}
              className="vb-input"
              style={{ paddingLeft: 28, paddingRight: query ? 28 : 8 }}
              data-testid="hold-prep-fill-search" />
            {query && (
              <button onClick={() => setQuery("")}
                className="absolute"
                style={{ right: 6, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", opacity: 0.5 }}
                data-testid="hold-prep-fill-search-clear" aria-label="Clear search">
                <X size={13} />
              </button>
            )}
          </div>
          {pendingCount > 0 && (
            <button className="vb-btn sm shrink-0"
              onClick={submitAll} disabled={bulkBusy}
              title={stagedCount ? `Submit ${stagedCount} entered temperature${stagedCount === 1 ? "" : "s"} at once` : "Enter temperatures on the rows below first"}
              data-testid="hold-prep-fill-submit-all">
              <Send size={13} /> {bulkBusy ? "Submitting…" : `Submit all${stagedCount ? ` (${stagedCount})` : ""}`}
            </button>
          )}
        </div>
      </div>

      <div className="max-w-3xl mx-auto p-3 space-y-2 pb-24">
        {visible.length === 0 && (
          <div className="vb-card p-6 text-center text-[13px] opacity-70" data-testid="hold-prep-fill-empty">
            {query
              ? `No dishes match "${query}" in this bucket.`
              : "No dishes in this bucket."}
          </div>
        )}
        {visible.map((it) => (
          <HoldPrepRow key={it.id}
            ref={(r) => { if (r) rowRefs.current[it.id] = r; else delete rowRefs.current[it.id]; }}
            item={it} prepList={prepList} mode={mode}
            holdMin={holdMin} holdMax={holdMax}
            onFilled={refresh} onSkipped={refresh} />
        ))}
      </div>
    </div>,
    document.body,
  );
}

function HoldPrepRowImpl({ item, prepList, mode, holdMin, holdMax, onFilled, onSkipped }, ref) {
  const isDone = item.status !== "pending";
  const [temp, setTemp] = useState("");
  const [ocr, setOcr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [expand, setExpand] = useState(false);
  const [corrective, setCorrective] = useState("");
  const [showSkip, setShowSkip] = useState(false);
  const [skipReason, setSkipReason] = useState("");
  const [showEdit, setShowEdit] = useState(false);
  const [editOrder, setEditOrder] = useState(item.order_no || "");
  const [editDish, setEditDish] = useState(item.dish || "");
  const camRef = useRef();
  const upRef = useRef();

  const tempNum = temp === "" ? NaN : Number(temp);
  const breach = !Number.isNaN(tempNum) && (mode === "hot" ? tempNum < holdMin : tempNum > holdMax);
  const rangeLabel = mode === "hot" ? `≥ ${holdMin}°C` : `≤ ${holdMax}°C`;

  useEffect(() => { if (breach) setExpand(true); }, [breach]);

  const runOcr = async (fileList) => {
    const f = fileList?.[0]; if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("context", mode === "hot" ? "hot_hold" : "cold_hold");
      const { data } = await api.post("/ocr/temperature", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      let celsius = data?.temperature_celsius;
      if (celsius != null) {
        setTemp(String(celsius));
        setOcr({
          photo_path: data.image_path,
          parsed_value: celsius,
          confidence: data.confidence,
        });
        toast.success(`Read ${celsius}°C · ${data.confidence_tier || ""} confidence`);
      } else {
        toast.error(data?.retry_reason || "Couldn't read the temperature — please enter manually.");
      }
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "OCR failed. Try again or enter manually.");
    } finally { setBusy(false); }
  };

  const commit = async () => {
    if (temp === "" || Number.isNaN(tempNum)) { toast.error("Enter the temperature first."); return; }
    if (breach && !corrective.trim()) { setExpand(true); toast.error(`Out of ${rangeLabel} — corrective action required.`); return; }
    setBusy(true);
    try {
      const payload = {
        temp_c: tempNum,
        corrective_action: corrective.trim(),
        notes: "",
      };
      if (ocr) {
        payload.ocr_photo_path = ocr.photo_path;
        payload.ocr_parsed_value = ocr.parsed_value;
        payload.ocr_confidence = ocr.confidence;
        payload.ocr_edited = Number(ocr.parsed_value) !== tempNum;
      }
      await api.post(`/hot-cold-hold/hold-prep-lists/${prepList.id}/items/${item.id}/fill`, payload);
      toast.success(`${item.dish} logged.`);
      onFilled();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Couldn't save.");
    } finally { setBusy(false); }
  };

  useImperativeHandle(ref, () => ({
    hasStagedTemp: () => temp !== "" && !Number.isNaN(Number(temp)),
    submitSilent: async () => {
      if (temp === "" || Number.isNaN(tempNum)) return { skipped: true };
      if (breach && !corrective.trim()) { setExpand(true); return { blocked: true }; }
      try {
        const payload = {
          temp_c: tempNum,
          corrective_action: corrective.trim(),
          notes: "",
        };
        if (ocr) {
          payload.ocr_photo_path = ocr.photo_path;
          payload.ocr_parsed_value = ocr.parsed_value;
          payload.ocr_confidence = ocr.confidence;
          payload.ocr_edited = Number(ocr.parsed_value) !== tempNum;
        }
        await api.post(`/hot-cold-hold/hold-prep-lists/${prepList.id}/items/${item.id}/fill`, payload);
        return { ok: true };
      } catch (e) {
        return { error: e };
      }
    },
  }), [temp, tempNum, breach, corrective, ocr, prepList.id, item.id]);

  const skip = async () => {
    if (!skipReason.trim()) { toast.error("A skip reason is required."); return; }
    setBusy(true);
    try {
      await api.post(`/hot-cold-hold/hold-prep-lists/${prepList.id}/items/${item.id}/skip`, {
        reason: skipReason.trim(),
      });
      toast.success(`${item.dish} skipped.`);
      onSkipped();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Couldn't skip.");
    } finally { setBusy(false); setShowSkip(false); }
  };

  const saveEdit = async () => {
    const dish = editDish.trim();
    if (!dish) { toast.error("Dish name can't be empty."); return; }
    setBusy(true);
    try {
      await api.put(`/hot-cold-hold/hold-prep-lists/${prepList.id}/items/${item.id}`, {
        order_no: editOrder.trim(),
        dish,
      });
      toast.success("Item updated.");
      setShowEdit(false);
      onFilled();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Couldn't save changes.");
    } finally { setBusy(false); }
  };

  if (isDone) {
    const bs = item.batch_status || item.status;
    return (
      <div className="vb-card p-3 flex items-center gap-3 text-[13px]" data-testid={`hold-prep-fill-row-${item.id}`}>
        <div className="w-14 text-[11px] opacity-70 shrink-0">{item.order_no || "—"}</div>
        <div className="flex-1 min-w-0">
          <div className="truncate"><strong>{item.dish}</strong></div>
          <div className="text-[11px] opacity-60 flex items-center gap-2 flex-wrap">
            {item.holding_temp_c != null && <span>{item.holding_temp_c}°C</span>}
            {item.has_breach && <span style={{ color: "#991b1b" }}>· NC</span>}
            {item.skipped_reason && <span>· {item.skipped_reason}</span>}
          </div>
        </div>
        <span className={`vb-pill ${HOLD_STATUS_CLS[bs] || ""} text-[10px] shrink-0`}
          data-testid={`hold-prep-fill-row-status-${item.id}`}>
          {HOLD_STATUS_LABEL[bs] || bs}
        </span>
      </div>
    );
  }

  return (
    <div className="vb-card p-3" data-testid={`hold-prep-fill-row-${item.id}`}>
      <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
        <div className="w-14 text-[11px] opacity-70 shrink-0">{item.order_no || "—"}</div>
        <div className="flex-1 min-w-0">
          <div className="truncate text-[14px]"><strong>{item.dish}</strong></div>
          {prepList.hold_time && (
            <div className="text-[11px] opacity-60">Time: {prepList.hold_time}</div>
          )}
        </div>

        <div className="flex items-center gap-1 shrink-0">
          <input type="number" step="0.1" inputMode="decimal"
            placeholder="°C" value={temp} onChange={(e) => setTemp(e.target.value)}
            style={{ width: 82 }} className="vb-input"
            data-testid={`hold-prep-fill-temp-${item.id}`} />
          <input ref={camRef} type="file" accept="image/*" capture="environment"
            style={{ display: "none" }}
            onChange={(e) => runOcr(e.target.files)} />
          <input ref={upRef} type="file" accept="image/*"
            style={{ display: "none" }}
            onChange={(e) => runOcr(e.target.files)} />
          <button className="vb-btn ghost sm" onClick={() => camRef.current?.click()}
            title="Take photo" disabled={busy}
            data-testid={`hold-prep-fill-cam-${item.id}`}>
            <Camera size={14} />
          </button>
          <button className="vb-btn ghost sm" onClick={() => upRef.current?.click()}
            title="Upload photo" disabled={busy}
            data-testid={`hold-prep-fill-up-${item.id}`}>
            <Upload size={14} />
          </button>
          <button className="vb-btn sm" onClick={commit}
            disabled={busy || temp === ""}
            data-testid={`hold-prep-fill-save-${item.id}`}>
            <Check size={14} />
          </button>
          <button className="vb-btn ghost sm" onClick={() => setExpand((v) => !v)}
            title="More options"
            data-testid={`hold-prep-fill-more-${item.id}`}>
            <ChevronDown size={14} />
          </button>
        </div>
      </div>

      {breach && !expand && (
        <div className="mt-2 text-[11px] flex items-center gap-1" style={{ color: "#991b1b" }}>
          <AlertTriangle size={12} /> Out of range ({rangeLabel}) — expand to add a corrective action.
        </div>
      )}

      {expand && (
        <div className="mt-3 space-y-2 border-t pt-3" style={{ borderColor: "var(--vb-border)" }}>
          {breach && (
            <div>
              <label className="text-[11px] opacity-70">Corrective action (required — out of {rangeLabel})</label>
              <input className="vb-input" value={corrective} onChange={(e) => setCorrective(e.target.value)}
                placeholder={mode === "hot" ? "e.g. reheated to 75°C and re-checked" : "e.g. moved to chiller and re-checked at 4°C"}
                data-testid={`hold-prep-fill-corrective-${item.id}`} />
            </div>
          )}
          <div className="flex justify-end gap-2">
            <button className="vb-btn ghost sm" onClick={() => { setEditOrder(item.order_no || ""); setEditDish(item.dish || ""); setShowEdit(true); }} disabled={busy}
              data-testid={`hold-prep-fill-edit-${item.id}`}>
              <Pencil size={13} /> Edit details
            </button>
            <button className="vb-btn ghost sm" onClick={() => setShowSkip(true)} disabled={busy}
              data-testid={`hold-prep-fill-skip-${item.id}`}>
              <SkipForward size={13} /> Skip this dish
            </button>
          </div>
        </div>
      )}

      {showEdit && (
        <div className="mt-3 p-3 rounded space-y-2" style={{ background: "rgba(59,130,246,0.06)", border: "1px solid rgba(59,130,246,0.3)" }}>
          <div className="text-[11px] font-semibold opacity-80">Edit item details</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] opacity-70">Order #</label>
              <input className="vb-input" value={editOrder} onChange={(e) => setEditOrder(e.target.value)}
                data-testid={`hold-prep-fill-edit-order-${item.id}`} />
            </div>
            <div>
              <label className="text-[10px] opacity-70">Dish name</label>
              <input className="vb-input" value={editDish} onChange={(e) => setEditDish(e.target.value)}
                data-testid={`hold-prep-fill-edit-dish-${item.id}`} />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button className="vb-btn ghost sm" onClick={() => setShowEdit(false)} disabled={busy}>Cancel</button>
            <button className="vb-btn sm" onClick={saveEdit} disabled={busy}
              data-testid={`hold-prep-fill-edit-save-${item.id}`}>Save changes</button>
          </div>
        </div>
      )}

      {showSkip && (
        <div className="mt-3 p-3 rounded" style={{ background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.3)" }}>
          <label className="text-[11px] opacity-70">Reason (required)</label>
          <input className="vb-input" value={skipReason} onChange={(e) => setSkipReason(e.target.value)}
            placeholder="e.g. ingredient unavailable"
            data-testid={`hold-prep-fill-skip-reason-${item.id}`} autoFocus />
          <div className="flex justify-end gap-2 mt-2">
            <button className="vb-btn ghost sm" onClick={() => { setShowSkip(false); setSkipReason(""); }} disabled={busy}>Cancel</button>
            <button className="vb-btn sm" onClick={skip} disabled={busy || !skipReason.trim()}
              data-testid={`hold-prep-fill-skip-confirm-${item.id}`}>Confirm skip</button>
          </div>
        </div>
      )}
    </div>
  );
}

const HoldPrepRow = forwardRef(HoldPrepRowImpl);
export { HoldPrepRow };
